<script setup lang="ts">
import { ref, toRefs, watch } from 'vue'

import TransitionFlipCounter from '@satch-ui/components/TransitionFlipCounter.vue'
import { t } from '@storefront-satch/lib/translations'
import { useDebounceFn } from '@vueuse/core'

import { MinusIcon, PlusIcon, TrashIcon } from '@fond-of/satch-icons'

const props = defineProps<{
  id: string
  productName: string
  quantity: number
  isLoading: boolean
  isError: boolean
}>()

const { quantity, isLoading, isError } = toRefs(props)

const increaseClicked = ref(0)
const decreaseClicked = ref(0)
const startChange = ref(false)
const isUpdating = ref(false)

const emit = defineEmits<{
  increase: [increaseQuantity: number]
  decrease: [decreaseQuantity: number]
}>()

const updateStatus = useDebounceFn(
  (value: boolean, successCallback?: () => void) => {
    isUpdating.value = value

    if (isUpdating.value === false) {
      startChange.value = false
    }

    successCallback?.()
    isUpdating.value = false
  },
  500
)

watch(isError, () => {
  if (isError.value) {
    startChange.value = false
  }
})

watch(quantity, () => {
  if (quantity.value > 0) {
    startChange.value = true
  }
  updateStatus(true)
})

const onIncrease = async () => {
  increaseClicked.value += 1

  updateStatus(true, () => {
    emit('increase', increaseClicked.value + quantity.value)
    increaseClicked.value = 0
  })
}

const onDecrease = async () => {
  decreaseClicked.value += 1

  await updateStatus(true, () => {
    emit(
      'decrease',
      decreaseClicked.value >= quantity.value
        ? 0
        : quantity.value - decreaseClicked.value
    )
    decreaseClicked.value = 0
  })
}
</script>

<template>
  <div
    class="border-gray-light inline-flex select-none items-center border-2 text-black"
    role="group"
    :aria-label="t('cart.counter.productAmount')"
    :aria-describedby="props.id"
  >
    <button
      type="button"
      class="relative inline-flex cursor-pointer items-center justify-center rounded-full p-2 text-xs disabled:opacity-50"
      :disabled="isLoading || isUpdating"
      :aria-label="
        quantity > 1
          ? t('cart.counter.decreaseAmount')
          : t('cart.counter.removeProduct')
      "
      @click="onDecrease"
    >
      <MinusIcon
        v-if="quantity > 1"
        class="size-4 text-black"
        aria-hidden="true"
      />
      <TrashIcon
        v-else
        class="size-4 text-black"
        aria-hidden="true"
      />
    </button>

    <div
      class="duration-250 h-8 w-8 rounded-full text-sm transition-all ease-in"
      :class="{ 'duration-250 !bg-primary': startChange }"
    >
      <TransitionFlipCounter
        :count="quantity"
        :start="startChange"
        speed="fast"
        @update="updateStatus"
      />

      <span
        :id="props.id"
        class="sr-only"
        aria-live="polite"
      >
        {{ `${t('cart.counter.amount')}: ${quantity}` }}
      </span>
    </div>

    <button
      type="button"
      class="relative inline-flex cursor-pointer items-center justify-center rounded-full p-2 text-xl disabled:opacity-50"
      :disabled="isLoading || isUpdating"
      :aria-label="t('cart.counter.increaseAmount')"
      @click="onIncrease"
    >
      <PlusIcon
        class="size-4 text-black"
        aria-hidden="true"
      />
    </button>
  </div>
</template>
